import type { Session } from 'next-auth';

import { getAppConfig } from '@services/config';

import type { TagCommanderType, TrackingType } from '@app-types/tracking';
import type { UserType } from '@app-types/user';

import { isServer } from '@helpers/config';
import { localApiPaths } from '@helpers/path';
import { getServerSession } from '@helpers/session';

class TrackingManager {
    private readonly appConfig = getAppConfig();
    private readonly tag = this.getTagCommander();
    private isReady = false;
    private queue: { isRenewal: boolean; data: TrackingType }[] = [];

    private setupAttempts = 0;
    private readonly maxSetupAttempts = 3;

    public setup(): this {
        if (!this.appConfig.tracking.enabled) {
            return this;
        }

        void this.initialize();

        return this;
    }

    public async sendTracking(isRenewal: boolean, data: TrackingType) {
        const tag = await this.tag;

        if (!tag) {
            throw new Error('tracking must be sent from the browser');
        }

        if (!this.isReady) {
            this.queue.push({ isRenewal, data });
            return;
        }

        try {
            await tag.send(
                JSON.parse(
                    JSON.stringify({
                        site: this.getSite(),
                        univers: data.universe ?? 'Boutique_mobile',
                        parcours: data.journey ?? (isRenewal ? 'Renouvellement' : 'Acquisition'),
                        url: data.url ?? window.location.href,
                        page: data.page,
                        pageCategorie: data.pageCategory,
                        actionClic: data.action,
                        categorieClic: data.category,
                        nomClic: data.name,
                        ligneMarche: data.marketLine,
                        activiteB2BouB2C: data.isB2B ? 'B2B' : 'B2C',
                        etapeEcommerce: data.eCommerceStep,
                        pageVariante: data.variant,
                        variablesSpecifiques: data.variables,
                        produits: data.products,
                        panier: data.cart,
                    }),
                ),
            );
        } catch (error) {
            console.error('Failed to send tracking event:', error);
        }
    }

    private async initialize() {
        const tag = await this.tag;

        if (!tag) {
            return;
        }

        this.attemptSetup(tag);
    }

    private async getTrackingUser(): Promise<UserType | undefined> {
        try {
            if (isServer()) {
                const session = await getServerSession();
                return session?.user as UserType | undefined;
            }

            const response = await fetch(localApiPaths.authentification.session);
            if (!response.ok) {
                return undefined;
            }

            const session = (await response.json()) as Session | null;
            return session?.user as UserType | undefined;
        } catch (error) {
            console.error('Failed to retrieve tracking session:', error);
            return undefined;
        }
    }

    private getTagCommander(): Promise<TagCommanderType> | undefined {
        if (typeof window === 'undefined') {
            return undefined;
        }

        return import('@bytel/bytel-tag-commander').then((module) => module.default as TagCommanderType);
    }

    private attemptSetup(tag: TagCommanderType) {
        setTimeout(async () => {
            try {
                const user = await this.getTrackingUser();

                await tag.setup({
                    host: this.appConfig.tracking.host,
                    debug: this.appConfig.tracking.debug,
                    env: this.appConfig.tracking.env,
                    site: this.getSite(),
                    oauth: user
                        ? {
                              id_personne: user.sub,
                              user_type: user.user_type,
                              accessToken: user.accessToken,
                          }
                        : {},
                    shouldEmitEvents: true,
                    hasHistory: true,
                });

                this.isReady = true;

                await Promise.all(this.queue.map(({ isRenewal, data }) => this.sendTracking(isRenewal, data)));
                this.queue = [];
            } catch (error) {
                this.setupAttempts++;
                if (this.setupAttempts < this.maxSetupAttempts) {
                    setTimeout(() => this.attemptSetup(tag), 500 * this.setupAttempts);
                } else {
                    console.error(`Max tracking setup attempts reached. Clearing queue. Error: ${error}`);
                    this.queue = [];
                }
            }
        }, 0);
    }

    private getSite() {
        if (isServer()) {
            throw new Error('Could not get tracking site from server side');
        }

        const bankCartData = localStorage.getItem('yama_cart');
        const isActiveBankCart =
            bankCartData !== null && Number(JSON.parse(bankCartData)?.expiresTimestamp) > Date.now();
        return isActiveBankCart ? 'Partenaire_cfcm_boutique' : 'Boutique';
    }
}

export const trackingManager = new TrackingManager().setup();
